import React from 'react';
import PropTypes from 'prop-types';
import DaysViewHeading from './days_view_heading';
import DaysOfWeek from './days_of_week';
import MonthSelector from './month_selector';
import Day from './day';
import moment from 'moment-jalali';
import { ServerDate } from '../containers/ServerDate';

moment.loadPersian();

function toDays(timestamp) {
    if (typeof timestamp !== 'number' && typeof timestamp !== 'string')
        timestamp = timestamp.toDate();
    if (timestamp instanceof Date)
        timestamp = timestamp.getTime() / 1000;
    return Math.floor((timestamp - (ServerDate()).getTimezoneOffset() * 60) / 3600 / 24);
}

export function getDaysOfMonth(month) {
    const days = [];

    const current = month.clone().startOf('jMonth');
    const end = month.clone().endOf('jMonth');

    // Set start to the first day of week in the last month
    current.subtract((current.day() + 1) % 7, 'days');

    // Set end to the last day of week in the next month
    end.add(6 - (end.day() + 1) % 7, 'days');

    while (current.isBefore(end)) {
        days.push(current.clone());
        current.add(1, 'days');
    }
    return days;
}

export default class Calendar extends React.PureComponent {
    static propTypes = {
        min: PropTypes.object,
        max: PropTypes.object,
        styles: PropTypes.object,
        selectedMoment: PropTypes.object,
        defaultMonth: PropTypes.object,
        onSelect: PropTypes.func,
        containerProps: PropTypes.object,
        from: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        to: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    };

    static childContextTypes = {
        nextMonth: PropTypes.func.isRequired,
        prevMonth: PropTypes.func.isRequired,
        setCalendarMode: PropTypes.func.isRequired,
        setMonth: PropTypes.func.isRequired,
    };

    static defaultProps = {
        styles: {},
        containerProps: {},
    };

    state = {
        month: this.props.defaultMonth || this.props.selectedMoment || moment(this.props.min),
        selectedMoment: this.props.selectedMoment || null,
        mode: 'days',
    };

    getChildContext() {
        return {
            nextMonth: this.nextMonth.bind(this),
            prevMonth: this.prevMonth.bind(this),
            setCalendarMode: this.setMode.bind(this),
            setMonth: this.setMonth.bind(this),
        };
    }

    componentDidMount() {
        const close = event => {
            if (
                this.props.inputComponent && !this.props.inputComponent.contains(event.target) &&
                this.refs.calendar && !this.refs.calendar.contains(event.target) &&
                this.props.onClickOutside
            )
                this.props.onClickOutside();
        };
        window.addEventListener('click', close, true);
        this.cleanUp = () => {
            window.removeEventListener('click', close, true);
        };
    }

    componentWillUnmount() {
        this.cleanUp();
    }

    componentWillReceiveProps({selectedMoment, defaultMonth, min}) {
        if (this.props.selectedMoment !== selectedMoment) {
            this.selectDay(selectedMoment);
        }
        else if (defaultMonth && this.props.defaultMonth !== defaultMonth && this.state.month === this.props.defaultMonth) {
            this.setMonth(defaultMonth);
        }
        else if (min && this.props.min !== min && this.state.month.isSame(this.props.min)) {
            this.setMonth(min.clone());
        }
    }

    setMode(mode) {
        this.setState({mode});
    }

    setMonth(month) {
        this.setState({month});
    }

    nextMonth() {
        this.setState({
            month: this.state.month.clone().add(1, 'jMonth'),
        });
    }

    prevMonth() {
        this.setState({
            month: this.state.month.clone().subtract(1, 'jMonth'),
        });
    }

    selectDay(selectedMoment) {
        const {month} = this.state;

        if (selectedMoment.format('jYYYYjMM') !== month.format('jYYYYjMM')) {
            this.setState({month: selectedMoment});
        }

        this.setState({selectedMoment});
    }

    handleClickOnDay(selectedMoment) {
        const {onSelect} = this.props;
        this.selectDay(selectedMoment);
        if (onSelect) {
            onSelect(selectedMoment);
        }
    };

    days = null;
    lastRenderedMonth = null;

    renderMonthSelector() {
        const {month} = this.state;
        const {styles} = this.props;
        return <div ref='months'>
            <MonthSelector styles={styles} selectedMonth={month}/>
        </div>;
    }

    renderDays() {
        const {month, selectedMoment} = this.state;
        const {children, min, max, styles} = this.props;

        let days;

        if (this.lastRenderedMonth === month) {
            days = this.days;
        }
        else {
            days = getDaysOfMonth(month);
            this.days = days;
            this.lastRenderedMonth = month;
        }

        const selectedDay = selectedMoment && toDays(selectedMoment);
        const from = this.props.from && toDays(this.props.from);
        const to = this.props.to && toDays(this.props.to);
        return <div ref='days'>
            {children}
            <DaysViewHeading styles={styles} month={month}/>
            <DaysOfWeek styles={styles}/>
            <div style={{overflow: 'hidden'}}>
                {days.map(moment => {
                    const isCurrentMonth = moment.format('jMM') === month.format('jMM');
                    const day = toDays(moment);
                    const inRange = (
                        (from || to) &&
                        day >= (from || selectedDay) &&
                        day <= (to || selectedDay)
                    );
                    const inHoveredRange = (
                        (from || to) &&
                        day >= (from || this.state.hoveredDay) &&
                        day <= (to || this.state.hoveredDay)
                    );
                    const disabled = (min ? moment.isBefore(min) : false) || (max ? moment.isAfter(max) : false);
                    const selected = selectedMoment ? selectedMoment.isSame(moment, 'day') : false;

                    return <Day
                        key={moment.format('YYYYMMDD') }
                        onClick={this.handleClickOnDay.bind(this)}
                        day={moment}
                        disabled={disabled}
                        selected={selected}
                        isCurrentMonth={isCurrentMonth}
                        inRange={inRange}
                        inHoveredRange={inHoveredRange}
                        onMouseEnter={() => this.setState({hoveredDay: day})}
                        onMouseLeave={() => this.state.hoveredDay === day ? this.setState({hoveredDay: undefined}) : null}
                    />;
                })}
            </div>
        </div>;
    }

    render() {
        return <div ref='calendar'>
            {this.state.mode === 'monthSelector'
                ? this.renderMonthSelector()
                : this.renderDays()
            }
        </div>;
    }
}
